import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { App } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';

import {
  buildScanResult,
  isValidUrl,
  normalizeUrl,
  pslTopDomain,
  registryApiBase,
} from './utils.js';
import type {
  BaselineSnapshot,
  CompareState,
  HistoryItem,
  ScanApiReport,
  ScanResult,
} from './types.js';

type UsePublicScansState = {
  messageApi: MessageInstance;
  messageHolder: ReactElement;
  url: string;
  setUrl: Dispatch<SetStateAction<string>>;
  gpc: boolean;
  setGpc: Dispatch<SetStateAction<boolean>>;
  normalizedUrl: string;
  urlValid: boolean;
  captureScreenshot: boolean;
  setCaptureScreenshot: Dispatch<SetStateAction<boolean>>;
  captureTrace: boolean;
  setCaptureTrace: Dispatch<SetStateAction<boolean>>;
  readyProxy: boolean | null;
  loadingProxy: boolean;
  checkReadyViaRegistry: () => Promise<void>;
  loadHistory: (u?: string) => Promise<void>;
  history: HistoryItem[];
  baseline: BaselineSnapshot | null;
  setBaseline: Dispatch<SetStateAction<BaselineSnapshot | null>>;
  compare: CompareState | null;
  compareLoading: boolean;
  loadCompare: (u?: string) => Promise<void>;
  diffOpen: boolean;
  setDiffOpen: Dispatch<SetStateAction<boolean>>;
  scanning: boolean;
  runScan: () => Promise<void>;
  result: ScanResult | null;
  setResult: Dispatch<SetStateAction<ScanResult | null>>;
  resetResult: () => void;
  error: string | null;
};

function initialUrl() {
  if (typeof window === 'undefined') return 'https://example.com';
  const params = new URLSearchParams(window.location.search);
  return params.get('url') || params.get('u') || 'https://example.com';
}

function initialGpc() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return (params.get('gpc') ?? '0') === '1';
}

function readToggle(key: string) {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function usePublicScans(registryUrl: string): UsePublicScansState {
  const appCtx = App.useApp?.();

  const messageApi = useMemo(() => {
    const base = appCtx?.message as MessageInstance | undefined;
    const ensure = <T,>(fn: T | undefined, fallback: T) =>
      (typeof fn === 'function' ? fn : fallback) as T;

    const noop = () => void 0;
    const fallbackMessage = {
      success: noop,
      error: noop,
      info: noop,
      warning: noop,
      loading: noop,
      open: noop,
      destroy: noop,
    };

    return {
      ...(base ?? {}),
      success: ensure(base?.success, fallbackMessage.success),
      error: ensure(base?.error, fallbackMessage.error),
      info: ensure(base?.info, fallbackMessage.info),
      warning: ensure(base?.warning, fallbackMessage.warning),
      loading: ensure(base?.loading, fallbackMessage.loading),
      open: ensure(base?.open, fallbackMessage.open),
      destroy: ensure(base?.destroy, fallbackMessage.destroy),
    } as MessageInstance;
  }, [appCtx?.message]);

  const [url, setUrl] = useState<string>(initialUrl);
  const [gpc, setGpc] = useState<boolean>(initialGpc);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [readyProxy, setReadyProxy] = useState<boolean | null>(null);
  const [loadingProxy, setLoadingProxy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [captureScreenshot, setCaptureScreenshot] = useState<boolean>(() =>
    readToggle('cmp.scanner.captureScreenshot'),
  );
  const [captureTrace, setCaptureTrace] = useState<boolean>(() =>
    readToggle('cmp.scanner.captureTrace'),
  );
  const [baseline, setBaseline] = useState<BaselineSnapshot | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compare, setCompare] = useState<CompareState | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);

  const normalizedUrl = useMemo(() => normalizeUrl(url), [url]);
  const urlValid = useMemo(() => isValidUrl(normalizedUrl), [normalizedUrl]);

  const loadHistory = useCallback(
    async (u?: string) => {
      try {
        const base = new URL(u || normalizedUrl);
        const top = pslTopDomain(base.hostname);
        const listUrl = `${registryApiBase(registryUrl)}/v1/scans?topDomain=${encodeURIComponent(
          top,
        )}&take=10`;
        const r = await fetch(listUrl);
        if (!r.ok) {
          setHistory([]);
          return;
        }
        const rows = (await r.json()) as Array<{
          id?: string;
          startedAt: string;
          finalUrl: string;
          status: string;
          summary?: { newThirdPartyCount?: number };
        }>;
        setHistory(
          rows.map((h) => ({
            id: h.id,
            ts: h.startedAt,
            status: h.status === 'ok' ? 'ok' : 'fail',
            url: h.finalUrl,
            new3p: h.summary?.newThirdPartyCount ?? 0,
          })),
        );
      } catch {
        setHistory([]);
      }
    },
    [normalizedUrl, registryUrl],
  );

  const loadCompare = useCallback(
    async (u?: string) => {
      try {
        setCompareLoading(true);
        const base = new URL(u || normalizedUrl);
        const top = pslTopDomain(base.hostname);
        const listUrl = `${registryApiBase(registryUrl)}/v1/scans?topDomain=${encodeURIComponent(
          top,
        )}&take=20`;
        const r = await fetch(listUrl, { cache: 'no-store' });
        if (!r.ok) {
          setCompare(null);
          return;
        }
        const rows = (await r.json()) as Array<{
          id?: string;
          gpc?: boolean;
          summary?: { newThirdPartyCount?: number };
        }>;
        const off = rows.find((x) => x.gpc === false && x.id);
        const on = rows.find((x) => x.gpc === true && x.id);

        async function detailCounts(id?: string): Promise<{
          cookies: number;
          headers: number;
          headerTitles: string[];
          headerAll: string[];
        }> {
          if (!id) return { cookies: 0, headers: 0, headerTitles: [], headerAll: [] };
          try {
            const detail = await fetch(
              `${registryApiBase(registryUrl)}/v1/scans/${encodeURIComponent(id)}`,
              { cache: 'no-store' },
            );
            if (!detail.ok) return { cookies: 0, headers: 0, headerTitles: [], headerAll: [] };
            const j = (await detail.json()) as {
              analysis?: { cookies?: { issues?: unknown[] }; headers?: { issues?: unknown[] } };
            };
            const titles = Array.isArray(j.analysis?.headers?.issues)
              ? (j.analysis?.headers?.issues as unknown[])
                  .map((x) => (typeof x === 'string' ? x : String(x)))
                  .slice(0, 3)
              : [];
            const all = Array.isArray(j.analysis?.headers?.issues)
              ? (j.analysis?.headers?.issues as unknown[]).map((x) =>
                  typeof x === 'string' ? x : String(x),
                )
              : [];
            return {
              cookies: (j.analysis?.cookies?.issues?.length as number) || 0,
              headers: (j.analysis?.headers?.issues?.length as number) || 0,
              headerTitles: titles,
              headerAll: all,
            };
          } catch {
            return { cookies: 0, headers: 0, headerTitles: [], headerAll: [] };
          }
        }

        const [offDetail, onDetail] = await Promise.all([
          detailCounts(off?.id),
          detailCounts(on?.id),
        ]);

        setCompare({
          off: off
            ? {
                id: off.id,
                new3p: off.summary?.newThirdPartyCount ?? 0,
                cookieIssues: offDetail.cookies,
                headerIssues: offDetail.headers,
                headerTitles: offDetail.headerTitles,
                headerAll: offDetail.headerAll,
              }
            : undefined,
          on: on
            ? {
                id: on.id,
                new3p: on.summary?.newThirdPartyCount ?? 0,
                cookieIssues: onDetail.cookies,
                headerIssues: onDetail.headers,
                headerTitles: onDetail.headerTitles,
                headerAll: onDetail.headerAll,
              }
            : undefined,
        });
      } finally {
        setCompareLoading(false);
      }
    },
    [normalizedUrl, registryUrl],
  );

  const checkReadyViaRegistry = useCallback(async () => {
    try {
      setLoadingProxy(true);
      const href = `${registryApiBase(registryUrl)}/health/scan-api`;
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 7000);
      try {
        const r = await fetch(href, { method: 'GET', cache: 'no-store', signal: ctrl.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as { ok?: boolean };
        setReadyProxy(Boolean(j?.ok));
        messageApi[j?.ok ? 'success' : 'error'](
          j?.ok ? 'Scanner ready (via registry)' : 'Scanner not reachable',
        );
      } finally {
        clearTimeout(timeout);
      }
    } catch {
      setReadyProxy(false);
      messageApi.error('Registry: Scanner not reachable');
    } finally {
      setLoadingProxy(false);
    }
  }, [messageApi, registryUrl]);

  useEffect(() => {
    void checkReadyViaRegistry();
    void loadHistory();
    void loadCompare();
  }, [checkReadyViaRegistry, loadHistory, loadCompare]);

  useEffect(() => {
    try {
      localStorage.setItem('cmp.scanner.captureScreenshot', captureScreenshot ? '1' : '0');
      localStorage.setItem('cmp.scanner.captureTrace', captureTrace ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [captureScreenshot, captureTrace]);

  const runScan = useCallback(async () => {
    if (!urlValid) {
      messageApi.error('Please enter a valid URL');
      return;
    }
    setScanning(true);
    setError(null);
    try {
      const r = await fetch(`${registryApiBase(registryUrl)}/v1/scans/run`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          url: normalizedUrl,
          gpc,
          captureScreenshot,
          captureTrace,
        }),
      });
      if (!r.ok) throw new Error(`Scan failed (${r.status})`);
      const raw = (await r.json()) as ScanApiReport;
      const { scan } = buildScanResult(raw, normalizedUrl, gpc);
      setResult(scan);
      messageApi.success('Scan complete');
    } catch (err) {
      setResult(null);
      setError((err as Error)?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [captureScreenshot, captureTrace, gpc, messageApi, normalizedUrl, registryUrl, urlValid]);

  const resetResult = useCallback(() => setResult(null), []);

  return {
    messageApi,
    messageHolder: null,
    url,
    setUrl,
    gpc,
    setGpc,
    normalizedUrl,
    urlValid,
    captureScreenshot,
    setCaptureScreenshot,
    captureTrace,
    setCaptureTrace,
    readyProxy,
    loadingProxy,
    checkReadyViaRegistry,
    loadHistory,
    history,
    baseline,
    setBaseline,
    compare,
    compareLoading,
    loadCompare,
    diffOpen,
    setDiffOpen,
    scanning,
    runScan,
    result,
    setResult,
    resetResult,
    error,
  };
}
